/**
 * Area of Operation map selector component.
 *
 * Draw a rectangle by clicking two corners. After drawing, the rectangle
 * can be moved (drag the center handle) and resized (drag any corner handle).
 */

import React, { useState, useCallback, useEffect } from 'react';
import L from 'leaflet';
import { Form, Button, InputGroup } from 'react-bootstrap';
import {
    MapContainer,
    TileLayer,
    Rectangle,
    CircleMarker,
    useMapEvents,
    useMap,
} from 'react-leaflet';
import { AoOBounds } from '../../types/mapBundle';
import { parseCoordinateInput } from '../../utils/coordinateParser';

// Zoom level applied when the user jumps to a searched coordinate.
const SEARCH_ZOOM = 14;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface AoOMapSelectorProps {
    bounds: AoOBounds | null;
    onBoundsChange: (bounds: AoOBounds | null) => void;
}

interface DrawingState {
    firstCorner: L.LatLng | null;
    currentCorner: L.LatLng | null;
}

type Corner = 'nw' | 'ne' | 'sw' | 'se';

// ---------------------------------------------------------------------------
// Drawing handler (phase 1: click two corners)
// ---------------------------------------------------------------------------

function DrawingHandler({
    active,
    firstCorner,
    onFirstClick,
    onMouseMove,
    onSecondClick,
}: {
    active: boolean;
    firstCorner: L.LatLng | null;
    onFirstClick: (latlng: L.LatLng) => void;
    onMouseMove: (latlng: L.LatLng) => void;
    onSecondClick: (latlng: L.LatLng) => void;
}) {
    useMapEvents({
        click(e) {
            if (!active) return;
            if (!firstCorner) {
                onFirstClick(e.latlng);
            } else {
                onSecondClick(e.latlng);
            }
        },
        mousemove(e) {
            if (active && firstCorner) {
                onMouseMove(e.latlng);
            }
        },
    });
    return null;
}

// ---------------------------------------------------------------------------
// Corner handle (draggable circle for resizing)
// ---------------------------------------------------------------------------

function CornerHandle({
    position,
    onDrag,
}: {
    position: L.LatLngExpression;
    onDrag: (latlng: L.LatLng) => void;
}) {
    return (
        <CircleMarker
            center={position}
            radius={6}
            pathOptions={{
                color: '#198754',
                fillColor: '#fff',
                fillOpacity: 1,
                weight: 2,
            }}
            eventHandlers={{
                mousedown: (e) => {
                    const map = e.target._map;
                    if (!map) return;

                    // Prevent map panning while dragging corner
                    map.dragging.disable();
                    L.DomEvent.stopPropagation(e.originalEvent);

                    const onMove = (ev: L.LeafletMouseEvent) => {
                        onDrag(ev.latlng);
                    };
                    const onUp = () => {
                        map.off('mousemove', onMove);
                        map.off('mouseup', onUp);
                        map.dragging.enable();
                    };
                    map.on('mousemove', onMove);
                    map.on('mouseup', onUp);
                },
            }}
        />
    );
}

// ---------------------------------------------------------------------------
// Map controller — recenters the map when a coordinate search is triggered
// ---------------------------------------------------------------------------

interface FlyTarget {
    lat: number;
    lng: number;
    // Bumped on every search so re-searching the same coordinate still recenters.
    nonce: number;
}

function MapController({ target }: { target: FlyTarget | null }) {
    const map = useMap();
    useEffect(() => {
        if (target) {
            map.setView([target.lat, target.lng], SEARCH_ZOOM);
        }
    }, [target, map]);
    return null;
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const AoOMapSelector: React.FC<AoOMapSelectorProps> = ({ bounds, onBoundsChange }) => {
    // Phase 1: drawing state
    const [drawingState, setDrawingState] = useState<DrawingState>({
        firstCorner: null,
        currentCorner: null,
    });
    const [isDrawingPhase, setIsDrawingPhase] = useState(true);

    // Coordinate search (GPS / MGRS) → recenter the map
    const [searchValue, setSearchValue] = useState('');
    const [searchError, setSearchError] = useState<string | null>(null);
    const [flyTarget, setFlyTarget] = useState<FlyTarget | null>(null);

    const handleSearchChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            setSearchValue(e.target.value);
            if (searchError) setSearchError(null);
        },
        [searchError],
    );

    const handleSearch = useCallback(() => {
        const parsed = parseCoordinateInput(searchValue);
        if (!parsed) {
            setSearchError(
                'Unrecognised coordinates. Use lat/lng (e.g. 49.8175, 15.4730) or MGRS (e.g. 33UWP1234567890).',
            );
            return;
        }
        setSearchError(null);
        setFlyTarget((prev) => ({
            lat: parsed.lat,
            lng: parsed.lng,
            nonce: (prev?.nonce ?? 0) + 1,
        }));
    }, [searchValue]);

    const handleSearchKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                handleSearch();
            }
        },
        [handleSearch],
    );

    // Reset when bounds are cleared externally
    useEffect(() => {
        if (!bounds) {
            setDrawingState({ firstCorner: null, currentCorner: null });
            setIsDrawingPhase(true);
        }
    }, [bounds]);

    // --- Phase 1 callbacks: draw rectangle by clicking two corners -----------

    const handleFirstClick = useCallback(
        (latlng: L.LatLng) => {
            setDrawingState({ firstCorner: latlng, currentCorner: latlng });
            onBoundsChange(null);
        },
        [onBoundsChange],
    );

    const handleMouseMove = useCallback((latlng: L.LatLng) => {
        setDrawingState((prev) => ({ ...prev, currentCorner: latlng }));
    }, []);

    const handleSecondClick = useCallback(
        (latlng: L.LatLng) => {
            const first = drawingState.firstCorner;
            if (!first) return;

            const north = Math.max(first.lat, latlng.lat);
            const south = Math.min(first.lat, latlng.lat);
            const east = Math.max(first.lng, latlng.lng);
            const west = Math.min(first.lng, latlng.lng);

            setIsDrawingPhase(false);
            setDrawingState({ firstCorner: null, currentCorner: null });
            onBoundsChange({
                aoo_north: north,
                aoo_south: south,
                aoo_east: east,
                aoo_west: west,
            });
        },
        [drawingState.firstCorner, onBoundsChange],
    );

    // --- Phase 2: corner drag (resize) --------------------------------------

    const handleCornerDrag = useCallback(
        (corner: Corner, latlng: L.LatLng) => {
            if (!bounds) return;
            const { aoo_north, aoo_south, aoo_east, aoo_west } = bounds;

            let north = aoo_north,
                south = aoo_south,
                east = aoo_east,
                west = aoo_west;

            switch (corner) {
                case 'nw':
                    north = latlng.lat;
                    west = latlng.lng;
                    break;
                case 'ne':
                    north = latlng.lat;
                    east = latlng.lng;
                    break;
                case 'sw':
                    south = latlng.lat;
                    west = latlng.lng;
                    break;
                case 'se':
                    south = latlng.lat;
                    east = latlng.lng;
                    break;
            }

            // Ensure north > south and east > west (swap if user drags past opposite corner)
            if (north < south) {
                const t = north;
                north = south;
                south = t;
            }
            if (east < west) {
                const t = east;
                east = west;
                west = t;
            }

            onBoundsChange({
                aoo_north: north,
                aoo_south: south,
                aoo_east: east,
                aoo_west: west,
            });
        },
        [bounds, onBoundsChange],
    );

    // --- Phase 2: rectangle body drag (move whole rectangle) -----------------

    const handleRectDragStart = useCallback(
        (e: L.LeafletMouseEvent) => {
            if (!bounds || isDrawingPhase) return;
            const map = e.target._map || e.target;
            map.dragging.disable();
            L.DomEvent.stopPropagation(e.originalEvent);

            const startLatLng = e.latlng;
            const startBounds = { ...bounds };

            const onMove = (ev: L.LeafletMouseEvent) => {
                const dLat = ev.latlng.lat - startLatLng.lat;
                const dLng = ev.latlng.lng - startLatLng.lng;
                onBoundsChange({
                    aoo_north: startBounds.aoo_north + dLat,
                    aoo_south: startBounds.aoo_south + dLat,
                    aoo_east: startBounds.aoo_east + dLng,
                    aoo_west: startBounds.aoo_west + dLng,
                });
            };
            const onUp = () => {
                map.off('mousemove', onMove);
                map.off('mouseup', onUp);
                map.dragging.enable();
            };
            map.on('mousemove', onMove);
            map.on('mouseup', onUp);
        },
        [bounds, isDrawingPhase, onBoundsChange],
    );

    // --- Render -------------------------------------------------------------

    // Drawing phase: show preview rectangle
    const previewBounds: L.LatLngBoundsExpression | null =
        drawingState.firstCorner && drawingState.currentCorner
            ? [
                  [drawingState.firstCorner.lat, drawingState.firstCorner.lng],
                  [drawingState.currentCorner.lat, drawingState.currentCorner.lng],
              ]
            : null;

    // Completed phase: show editable rectangle
    const editBounds: L.LatLngBoundsExpression | null = bounds
        ? [
              [bounds.aoo_south, bounds.aoo_west],
              [bounds.aoo_north, bounds.aoo_east],
          ]
        : null;

    return (
        <div className="aoo-map-selector">
            <div className="aoo-coord-search mb-2">
                <InputGroup>
                    <Form.Control
                        type="text"
                        placeholder="Go to coordinates — e.g. 49.8175, 15.4730 or 33UWP1234567890"
                        value={searchValue}
                        onChange={handleSearchChange}
                        onKeyDown={handleSearchKeyDown}
                        isInvalid={!!searchError}
                        aria-label="Go to GPS or MGRS coordinates"
                    />
                    <Button
                        variant="outline-secondary"
                        onClick={handleSearch}
                        disabled={!searchValue.trim()}
                    >
                        Go to
                    </Button>
                </InputGroup>
                {searchError && (
                    <div className="aoo-coord-error text-danger small mt-1">{searchError}</div>
                )}
            </div>

            <div className="aoo-map-container">
                <MapContainer
                    center={[49.8175, 15.473]}
                    zoom={8}
                    scrollWheelZoom={true}
                    doubleClickZoom={false}
                >
                    <TileLayer
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />

                    {/* Recenter on coordinate search */}
                    <MapController target={flyTarget} />

                    {/* Phase 1: drawing handler */}
                    <DrawingHandler
                        active={isDrawingPhase}
                        firstCorner={drawingState.firstCorner}
                        onFirstClick={handleFirstClick}
                        onMouseMove={handleMouseMove}
                        onSecondClick={handleSecondClick}
                    />

                    {/* Phase 1: preview rectangle (dashed) */}
                    {previewBounds && (
                        <Rectangle
                            bounds={previewBounds}
                            pathOptions={{
                                color: '#0d6efd',
                                weight: 2,
                                fillOpacity: 0.1,
                                dashArray: '6, 4',
                            }}
                        />
                    )}

                    {/* Phase 2: editable rectangle — drag body to move, drag corners to resize */}
                    {editBounds && (
                        <>
                            <Rectangle
                                bounds={editBounds}
                                pathOptions={{
                                    color: '#198754',
                                    weight: 2,
                                    fillOpacity: 0.1,
                                    className: 'aoo-rect-draggable',
                                }}
                                eventHandlers={{
                                    mousedown: handleRectDragStart,
                                }}
                            />

                            {/* Corner handles for resizing */}
                            <CornerHandle
                                position={[bounds!.aoo_north, bounds!.aoo_west]}
                                onDrag={(ll) => handleCornerDrag('nw', ll)}
                            />
                            <CornerHandle
                                position={[bounds!.aoo_north, bounds!.aoo_east]}
                                onDrag={(ll) => handleCornerDrag('ne', ll)}
                            />
                            <CornerHandle
                                position={[bounds!.aoo_south, bounds!.aoo_west]}
                                onDrag={(ll) => handleCornerDrag('sw', ll)}
                            />
                            <CornerHandle
                                position={[bounds!.aoo_south, bounds!.aoo_east]}
                                onDrag={(ll) => handleCornerDrag('se', ll)}
                            />
                        </>
                    )}
                </MapContainer>
            </div>
        </div>
    );
};

export default AoOMapSelector;
